import axios from 'axios';
import Parse from 'parse';
import { PRODUCT_BASE_ID } from '../constant';
import { NO_PACK } from '../packNav';
import { getUrl } from '../utils';
import { actionWithLoader, push } from './utils';

export const Pack = Parse.Object.extend('Pack');

export { NO_PACK };

export const PACK_COVER_URL = 'https://storage.googleapis.com/silhouette-users/packs/';

/**
 * set (or unset when packId is empty) the pack pointer of a Template, Background, Overlay or Decoration
 * @param {Parse.Object} parseObject
 * @param {string} packId
 */
export const setPack = (parseObject, packId) => {
  if (packId) {
    const pack = new Pack();
    pack.id = packId;
    parseObject.set('pack', pack);
  } else {
    parseObject.unset('pack');
  }
};

/**
 * restrict an item query to a pack, or to the items without a live pack
 * (no pointer, or a pointer to a deleted pack)
 * @param {Parse.Query} query
 * @param {string} packId pack id or NO_PACK
 */
export const filterByPack = (query, packId) => {
  if (packId === NO_PACK) {
    query.doesNotMatchQuery('pack', new Parse.Query(Pack).notEqualTo('deleted', true));
  } else if (packId) {
    const pack = new Pack();
    pack.id = packId;
    query.equalTo('pack', pack);
  }
};

// A template counts when it belongs to the base product, as in the templates lists.
const ofBaseProduct = (templates) => templates.filter((template) => (template.get('products') ?? []).some((product) => product.id === PRODUCT_BASE_ID)).length;

// Counted with the same filters as the pack page's lists
// (backgrounds of old templates excluded, templates of the base product only).
const countInPack = (className, pack) => {
  const query = new Parse.Query(className).equalTo('pack', pack).notEqualTo('deleted', true);
  if (className === 'Template') return query.select('products').limit(1000).find().then(ofBaseProduct);
  if (className === 'Background') query.doesNotExist('template');
  return query.count();
};

// The items without a live pack, counted with the same filters as their lists
// (backgrounds of old templates excluded, templates of the base product only).
export const countWithoutPack = async () => {
  const withoutPack = (className) => {
    const query = new Parse.Query(className).notEqualTo('deleted', true);
    filterByPack(query, NO_PACK);
    return query;
  };
  const [templates, backgrounds, overlays, decorations] = await Promise.all([
    withoutPack('Template').select('products').limit(1000).find().then(ofBaseProduct),
    withoutPack('Background').doesNotExist('template').count(),
    withoutPack('Overlay').count(),
    withoutPack('Decoration').count(),
  ]);
  return { templates, backgrounds, overlays, decorations };
};

const uploadCover = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(getUrl() + '/api/uploadPackCover', formData);
  return response.data.fileName;
};

const applyValues = async (pack, values) => {
  pack.set('name', values.name);
  pack.set('published', !!values.published);
  pack.set('isBase', !!values.isBase);
  if (values.icon) {
    pack.set('icon', values.icon);
  } else {
    pack.unset('icon');
  }
  if (values.cover instanceof File) {
    pack.set('cover', await uploadCover(values.cover));
  }
};

export const getPacks = () => {
  return async (dispatch) => {
    const packs = await new Parse.Query(Pack).notEqualTo('deleted', true).ascending('order').find();
    const withCounts = await Promise.all(
      packs.map(async (pack) => {
        const [templates, backgrounds, overlays, decorations] = await Promise.all([countInPack('Template', pack), countInPack('Background', pack), countInPack('Overlay', pack), countInPack('Decoration', pack)]);
        return { ...pack.toJSON(), counts: { templates, backgrounds, overlays, decorations } };
      })
    );
    dispatch({ type: 'GET_PACKS', packs: withCounts });
    return withCounts;
  };
};

export const countPacks = () => new Parse.Query(Pack).notEqualTo('deleted', true).count();

export const createPack = (values) => {
  return actionWithLoader(async (dispatch, getState) => {
    const pack = new Pack();
    await applyValues(pack, values);
    const { packs } = getState().packs;
    pack.set('order', packs.reduce((max, p) => Math.max(max, p.order ?? -1), -1) + 1);
    await pack.save();
    await dispatch(getPacks()); // the hook may have unset isBase on another pack
  });
};

export const editPack = (id) => {
  return async (dispatch) => {
    const pack = await new Parse.Query(Pack).get(id);
    dispatch({ type: 'EDIT_PACK', pack: pack.toJSON() });
  };
};

export const updatePack = (pack, values) => {
  return actionWithLoader(async (dispatch) => {
    const updated = new Pack();
    updated.id = pack.objectId;
    await applyValues(updated, values);
    await updated.save();
    await dispatch(getPacks());
  });
};

export const deletePack = (id) => {
  return actionWithLoader(async (dispatch) => {
    const pack = new Pack();
    pack.id = id;
    pack.set('deleted', true);
    await pack.save();
    dispatch({ type: 'DELETE_PACK', id });
  });
};

export const reorderPacks = (packs) => {
  return async (dispatch) => {
    await Promise.all(
      packs.map((p, index) => {
        const pack = new Pack();
        pack.id = p.objectId;
        pack.set('order', index);
        return pack.save();
      })
    );
    await dispatch(getPacks());
  };
};

export function goToPacks() {
  return push('/packs');
}
